import { z } from "zod";
import type { Operation } from "../types";
import type {
  CostAnalytics,
  ToolsAnalytics,
  ProjectSummary,
  SessionWithFacet,
  ReplayData,
  ExportPayload,
  HistoryEntry,
} from "@lens/types/claude";
import type { ActivityAnalytics, DashboardSnapshot } from "@lens/lib/snapshot";
import { slugParams } from "./_common";

// Lens response payloads are pass-through to existing Lens domain types
// (DashboardSnapshot, ProjectSummary, SessionWithFacet, etc.). We use
// z.custom<T>() to type at compile time without re-validating server payloads
// the client already trusts — these come from local SQLite data the user owns.
//
// Bodies and query strings DO get validated — they're user input.

/**
 * Version stamp returned by `/lens/api/version`. SWR cache-revalidation key.
 * Note that the document product's parallel endpoint returns
 * `{ version: string }` instead — different shape, same purpose. The two
 * endpoints stamp different data sources (Lens reads ~/.claude/ mtime;
 * document reads SQL row versions).
 */
export interface VersionResp {
  mtimeMs: number;
  projectCount: number;
}

interface StatsResp {
  stats: unknown;
  computed: unknown;
}
interface ProjectsListResp {
  projects: ProjectSummary[];
}
interface SessionsListResp {
  sessions: SessionWithFacet[];
  total: number;
}
interface SessionByIdResp {
  session: SessionWithFacet;
}
interface SettingsResp {
  settings: unknown;
  storageBytes: number;
  skills: unknown;
  plugins: unknown;
  excellent: unknown;
}
interface MemoryListResp {
  memories: unknown[];
}
interface MemoryPatchBody {
  projectSlug: string;
  file: string;
  content: string;
}
interface HistoryResp {
  history: HistoryEntry[];
}
interface HistoryQuery {
  limit?: number;
}
interface TodosResp {
  todos: unknown[];
}
interface PlansResp {
  plans: unknown[];
}
interface ExportPreviewResp {
  sessionCount: number;
  facetCount: number;
  historyEntries: number;
  hasStatsCache: boolean;
  totalSessionsIndexed: number;
  scopedProject: { slug: string; name: string } | null;
}
interface ExportQuery {
  from?: string;
  to?: string;
}
interface ExportFullBody {
  dateRange?: { from?: string; to?: string };
}
interface ExcellentSkillsBody {
  action: "install" | "uninstall";
  force?: boolean;
}
/**
 * Response from `/lens/api/excellent-skills`. Note `success` (not `ok`) — the
 * outer envelope already carries `ok`, so the payload uses a different name
 * to avoid the field-name collision.
 */
interface ExcellentSkillsResp {
  success: boolean;
  action: string;
  exit_code: number | null;
  stdout: string;
  stderr: string;
  state: unknown;
}

const versionOp: Operation<void, void, void, VersionResp> = {
  method: "GET",
  path: () => "/lens/api/version",
  response: z.custom<VersionResp>(),
};

const snapshotOp: Operation<void, void, void, DashboardSnapshot> = {
  method: "GET",
  path: () => "/lens/api/snapshot",
  response: z.custom<DashboardSnapshot>(),
};

const costsOp: Operation<void, void, void, CostAnalytics> = {
  method: "GET",
  path: () => "/lens/api/costs",
  response: z.custom<CostAnalytics>(),
};

const toolsOp: Operation<void, void, void, ToolsAnalytics> = {
  method: "GET",
  path: () => "/lens/api/tools",
  response: z.custom<ToolsAnalytics>(),
};

const activityOp: Operation<void, void, void, ActivityAnalytics> = {
  method: "GET",
  path: () => "/lens/api/activity",
  response: z.custom<ActivityAnalytics>(),
};

const statsOp: Operation<void, void, void, StatsResp> = {
  method: "GET",
  path: () => "/lens/api/stats",
  response: z.custom<StatsResp>(),
};

const projectsListOp: Operation<void, void, void, ProjectsListResp> = {
  method: "GET",
  path: () => "/lens/api/projects",
  response: z.custom<ProjectsListResp>(),
};

/**
 * Project detail shape — mirrors the locally-derived structure built in
 * products/lens/app/projects/[slug]/page.tsx. Living here keeps callers from
 * having to redefine it.
 */
export interface ProjectDetailResp {
  project_path: string;
  display_name: string;
  sessions: SessionWithFacet[];
  tool_counts: Record<string, number>;
  cost_by_session: Array<{
    session_id: string;
    start_time: string;
    cost: number;
    messages: number;
  }>;
  branches: Array<{ branch: string; turns: number }>;
}

const projectBySlugOp: Operation<{ slug: string }, void, void, ProjectDetailResp> = {
  method: "GET",
  path: ({ slug }) => `/lens/api/projects/${slug}`,
  params: slugParams,
  response: z.custom<ProjectDetailResp>(),
};

const sessionsListOp: Operation<void, void, void, SessionsListResp> = {
  method: "GET",
  path: () => "/lens/api/sessions",
  response: z.custom<SessionsListResp>(),
};

const sessionByIdOp: Operation<{ id: string }, void, void, SessionByIdResp> = {
  method: "GET",
  path: ({ id }) => `/lens/api/sessions/${id}`,
  params: z.object({ id: z.string().min(1) }),
  response: z.custom<SessionByIdResp>(),
};

const sessionReplayOp: Operation<{ id: string }, void, void, ReplayData> = {
  method: "GET",
  path: ({ id }) => `/lens/api/sessions/${id}/replay`,
  params: z.object({ id: z.string().min(1) }),
  response: z.custom<ReplayData>(),
};

const settingsOp: Operation<void, void, void, SettingsResp> = {
  method: "GET",
  path: () => "/lens/api/settings",
  response: z.custom<SettingsResp>(),
};

const memoryListOp: Operation<void, void, void, MemoryListResp> = {
  method: "GET",
  path: () => "/lens/api/memory",
  response: z.custom<MemoryListResp>(),
};

const memoryPatchOp: Operation<void, void, MemoryPatchBody, { ok: true }> = {
  method: "PATCH",
  path: () => "/lens/api/memory",
  body: z.object({
    projectSlug: z
      .string()
      .min(1)
      .refine((s) => !/[/\\]/.test(s), "no slashes allowed in projectSlug"),
    file: z
      .string()
      .min(1)
      .refine((f) => f.endsWith(".md"), "must be a .md file")
      .refine((f) => !/[/\\]/.test(f), "no slashes allowed in file"),
    content: z.string(),
  }),
  response: z.object({ ok: z.literal(true) }),
};

const historyOp: Operation<void, HistoryQuery, void, HistoryResp> = {
  method: "GET",
  path: () => "/lens/api/history",
  query: z.object({ limit: z.coerce.number().optional() }),
  response: z.custom<HistoryResp>(),
};

const todosOp: Operation<void, void, void, TodosResp> = {
  method: "GET",
  path: () => "/lens/api/todos",
  response: z.custom<TodosResp>(),
};

const plansOp: Operation<void, void, void, PlansResp> = {
  method: "GET",
  path: () => "/lens/api/plans",
  response: z.custom<PlansResp>(),
};

const exportPreviewOp: Operation<void, ExportQuery, void, ExportPreviewResp> = {
  method: "GET",
  path: () => "/lens/api/export",
  query: z.object({
    from: z.string().optional(),
    to: z.string().optional(),
  }),
  response: z.custom<ExportPreviewResp>(),
};

const exportFullOp: Operation<void, void, ExportFullBody, ExportPayload> = {
  method: "POST",
  path: () => "/lens/api/export",
  body: z.object({
    dateRange: z
      .object({
        from: z.string().optional(),
        to: z.string().optional(),
      })
      .optional(),
  }),
  response: z.custom<ExportPayload>(),
};

const importPayloadOp: Operation<void, void, ExportPayload, unknown> = {
  method: "POST",
  path: () => "/lens/api/import",
  body: z.custom<ExportPayload>(),
  response: z.custom<unknown>(),
};

const excellentSkillsOp: Operation<
  void,
  void,
  ExcellentSkillsBody,
  ExcellentSkillsResp
> = {
  method: "POST",
  path: () => "/lens/api/excellent-skills",
  body: z.object({
    action: z.enum(["install", "uninstall"]),
    force: z.boolean().optional(),
  }),
  response: z.custom<ExcellentSkillsResp>(),
};

export const lens = {
  version: versionOp,
  snapshot: snapshotOp,
  costs: costsOp,
  tools: toolsOp,
  activity: activityOp,
  stats: statsOp,
  projectsList: projectsListOp,
  projectBySlug: projectBySlugOp,
  sessionsList: sessionsListOp,
  sessionById: sessionByIdOp,
  sessionReplay: sessionReplayOp,
  settings: settingsOp,
  memoryList: memoryListOp,
  memoryPatch: memoryPatchOp,
  history: historyOp,
  todos: todosOp,
  plans: plansOp,
  exportPreview: exportPreviewOp,
  exportFull: exportFullOp,
  importPayload: importPayloadOp,
  excellentSkills: excellentSkillsOp,
};
